type TelegramWebAppBridge = {
  initData?: string;
  ready?: () => void;
  expand?: () => void;
  close?: () => void;
};

type TelegramBridgeWindow = Window & {
  Telegram?: {
    WebApp?: TelegramWebAppBridge;
  };
};

export type TelegramLaunchState = {
  isTelegramContext: boolean;
  shouldAutoAuth: boolean;
};

function getBridgeWindow() {
  return window as TelegramBridgeWindow;
}

function queryCandidates(rawValue: string) {
  const raw = rawValue.startsWith('#') ? rawValue.slice(1) : rawValue;
  const candidates = [raw];

  const queryIndex = raw.indexOf('?');
  if (queryIndex >= 0) {
    candidates.push(raw.slice(queryIndex + 1));
  }

  const tgIndex = raw.indexOf('tgWebAppData=');
  if (tgIndex > 0) {
    candidates.push(raw.slice(tgIndex));
  }

  const initIndex = raw.indexOf('initData=');
  if (initIndex > 0) {
    candidates.push(raw.slice(initIndex));
  }

  return candidates;
}

function readTelegramInitDataFromLocation() {
  const rawParts = [
    window.location.search,
    window.location.hash,
  ].filter(Boolean);

  for (const rawPart of rawParts) {
    for (const raw of queryCandidates(rawPart)) {
      const normalized = raw.startsWith('?') ? raw.slice(1) : raw;
      const params = new URLSearchParams(normalized);
      const initData = params.get('tgWebAppData') || params.get('initData');
      if (initData?.trim()) return initData.trim();
    }
  }

  return '';
}

function hasTelegramBootstrapHint() {
  return Boolean(readTelegramInitDataFromLocation());
}

export function getTelegramWebApp() {
  return getBridgeWindow().Telegram?.WebApp || null;
}

export function isTelegramMiniAppContext() {
  return resolveTelegramLaunchState({
    initData: getTelegramInitData(),
    hinted: hasTelegramBootstrapHint(),
  }).isTelegramContext;
}

export function getTelegramInitData() {
  return String(getTelegramWebApp()?.initData || '').trim() || readTelegramInitDataFromLocation();
}

export function resolveTelegramLaunchState({
  initData,
  hinted = false,
}: {
  initData?: string | null;
  hinted?: boolean;
  webApp?: TelegramWebAppBridge | null;
}): TelegramLaunchState {
  const normalizedInitData = String(initData || '').trim();
  const hasInitData = Boolean(normalizedInitData);

  return {
    isTelegramContext: hasInitData || Boolean(hinted),
    shouldAutoAuth: hasInitData,
  };
}

export async function waitForTelegramMiniApp({
  timeoutMs = 3000,
  pollMs = 50,
}: {
  timeoutMs?: number;
  pollMs?: number;
} = {}) {
  const initialHint = hasTelegramBootstrapHint();
  let webApp = getTelegramWebApp();
  let initData = getTelegramInitData();
  const waitMs = initialHint || webApp ? timeoutMs : Math.min(timeoutMs, 1500);
  const deadline = Date.now() + waitMs;

  while (Date.now() < deadline) {
    if (initData) {
      return {
        available: true,
        hinted: true,
        initData,
        webApp,
      };
    }

    await new Promise((resolve) => window.setTimeout(resolve, pollMs));
    webApp = getTelegramWebApp();
    initData = getTelegramInitData();
  }

  const hinted = initialHint || Boolean(initData);
  return {
    available: Boolean(initData),
    hinted,
    initData,
    webApp,
  };
}

export function prepareTelegramMiniApp() {
  const webApp = getTelegramWebApp();
  if (!webApp) return;

  webApp.ready?.();
  webApp.expand?.();
}

export function closeTelegramMiniApp() {
  getTelegramWebApp()?.close?.();
}
